import {
  getPartyParticipantsHref,
  getPrimaryActionHref,
  SOCIAL_PARTY_SIZE_HINT,
  type PartyDashboardPrimaryAction,
} from "./party-dashboard";
import { isPaidParty, isPartyMemberCapNearLimit } from "./party-entitlements";
import type { PartyPassCheckoutSource } from "./party-pass-checkout";
import type { PartyPlan } from "./types/parties-api";

export const JEV_DECISION_PAGES = [
  "party_home",
  "party_vote",
  "party_leaderboard",
  "party_chat",
  "party_invite",
  "party_votes",
] as const;

export type JevDecisionPage = (typeof JEV_DECISION_PAGES)[number];

export const JEV_GROWTH_CTAS = [
  "invite_more",
  "share_invite",
  "try_chat",
  "nudge_voters",
  "vote_remaining",
  "follow_show",
  "react_live",
  "celebrate",
  "watch_red_carpet",
  "stay_quiet",
] as const;

export type JevGrowthCta = (typeof JEV_GROWTH_CTAS)[number];

export const JEV_PASS_REASONS = [
  "cap",
  "chat",
  "live_reactions",
  "leaderboard",
  "recap",
  "blocked_join",
  "none",
] as const;

export type JevPassReason = (typeof JEV_PASS_REASONS)[number];

export const JEV_PASS_INTENSITIES = [
  "stay_quiet",
  "soft_suggest",
  "interrupt",
] as const;

export type JevPassIntensity = (typeof JEV_PASS_INTENSITIES)[number];

export const JEV_SHOW_NIGHT_CTAS = [
  "vote_remaining",
  "follow_show",
  "try_chat",
  "react_live",
  "unlock_chat",
  "watch_red_carpet",
  "stay_quiet",
] as const;

export type JevShowNightCta = (typeof JEV_SHOW_NIGHT_CTAS)[number];

export const JEV_PRE_SHOW_FOCUSES = ["grow", "vote", "wait"] as const;

export type JevPreShowFocus = (typeof JEV_PRE_SHOW_FOCUSES)[number];

export type JevDecisionLayerCta = JevGrowthCta | JevShowNightCta;

export const JEV_DECISION_SOURCES = ["jev", "fallback", "disabled"] as const;

export type JevDecisionSource = (typeof JEV_DECISION_SOURCES)[number];

export const JEV_DECISION_SURFACE_KINDS = [
  "none",
  "growth",
  "show_night",
  "pass_interrupt",
  "pass_soft",
] as const;

export type JevDecisionSurfaceKind =
  (typeof JEV_DECISION_SURFACE_KINDS)[number];

/** Minimum P(true) before growth coaching may render. */
export const JEV_MIN_SURFACE_PROBABILITY = 0.55;
/** Minimum selected-option probability before we trust a choice. */
export const JEV_MIN_CTA_PROBABILITY = 0.5;
/** Minimum TypeSafe confidence before we trust a choice. */
export const JEV_MIN_CTA_CONFIDENCE = 0.5;
/** Urgency score (0–3) at or above which the strip uses the prominent treatment. */
export const JEV_PROMINENT_URGENCY_SCORE = 2;
/** Urgency score at or above which we may replace the existing primary CTA. */
export const JEV_OVERRIDE_URGENCY_SCORE = 2;
/**
 * Hours before show start when a red carpet CTA may appear.
 * There is no red carpet start time, so this uses hoursUntilShow.
 */
export const JEV_RED_CARPET_HOURS_BEFORE_SHOW = 6;
/** Hours after show start when arrivals are still worth a nudge. */
export const JEV_RED_CARPET_HOURS_AFTER_START = 2;
/** An open ballot inside this many hours of lock outranks the red carpet. */
export const JEV_RED_CARPET_URGENT_BALLOT_HOURS = 2;
/**
 * Further out than this, an underfilled host should invite instead of
 * watching arrivals. Inside this window the carpet can compete.
 */
export const JEV_RED_CARPET_HOST_INVITE_PRIORITY_HOURS = 2;
/** Party growth target. Hosts below this stay on invite until showtime is close. */
export const JEV_RED_CARPET_GROWTH_PARTY_SIZE = 3;

const HOST_ONLY_GROWTH_CTAS = new Set<JevGrowthCta>([
  "invite_more",
  "share_invite",
  "nudge_voters",
]);

const CTA_TO_PRIMARY_ACTION: Partial<
  Record<JevGrowthCta, PartyDashboardPrimaryAction>
> = {
  invite_more: "invite",
  share_invite: "invite",
  nudge_voters: "nudge",
  vote_remaining: "vote",
  follow_show: "leaderboard",
  try_chat: "chat",
  celebrate: "invite",
};

const SHOW_NIGHT_CTA_TO_PRIMARY: Partial<
  Record<JevShowNightCta, PartyDashboardPrimaryAction>
> = {
  vote_remaining: "vote",
  follow_show: "leaderboard",
  try_chat: "chat",
  unlock_chat: "chat",
};

const JEV_PASS_REASON_TO_CHECKOUT_SOURCE: Record<
  Exclude<JevPassReason, "none">,
  PartyPassCheckoutSource
> = {
  cap: "jev_cap",
  chat: "jev_chat",
  live_reactions: "jev_live_reactions",
  leaderboard: "jev_leaderboard",
  recap: "jev_recap",
  blocked_join: "jev_blocked_join",
};

export interface JevChoiceAnswer<T extends string> {
  type: "choice";
  choice: T;
  probabilities?: Partial<Record<T, number>>;
}

export interface JevScoreAnswer {
  type: "score";
  score: number;
  probabilities?: Record<string, number>;
}

export interface JevBooleanAnswer {
  type: "boolean";
  probability: number;
}

export interface JevDecisionLayerSignals {
  [key: string]: string | number | boolean | null;
  isOwner: boolean;
  plan: PartyPlan;
  joinedParticipantCount: number;
  pendingInviteCount: number;
  hoursSinceCreated: number;
  hoursUntilShow: number | null;
  hoursUntilLock: number | null;
  showHasStarted: boolean;
  showIsComplete: boolean;
  isLocked: boolean;
  userBallotComplete: boolean;
  nonVoterCount: number;
  chatUnlocked: boolean;
  chatHasActivity: boolean;
  chatUnused: boolean;
  atOrOverCap: boolean;
  nearMemberCap: boolean;
  blockedJoinCount: number;
  socialPartySizeHint: number;
  announcedResultsCount: number;
  totalCategories: number;
  page: JevDecisionPage;
  /** True when the show has at least one red carpet video URL. */
  hasRedCarpet: boolean;
  redCarpetMediaCount: number;
  /** Pre-show through early show night, before any winner is announced. */
  redCarpetWindowOpen: boolean;
}

export interface JevDecisionLayerStateInput {
  isOwner: boolean;
  plan: PartyPlan;
  joinedParticipantCount: number;
  pendingInviteCount: number;
  createdAt: Date | string;
  showStart: Date | string | null | undefined;
  votingClosesAt: Date | string | null;
  now?: Date | string;
  showHasStarted: boolean;
  showIsComplete: boolean;
  isLocked: boolean;
  userBallotComplete: boolean;
  nonVoterCount: number;
  chatUnlocked: boolean;
  chatHasActivity: boolean;
  memberCap: number | null;
  totalParticipantCount: number;
  blockedJoinCount: number;
  announcedResultsCount: number;
  totalCategories: number;
  page: JevDecisionPage;
  /** Parsed red carpet URL count. Omitted or 0 means the show has no media. */
  redCarpetMediaCount?: number;
}

export interface JevDecisionLayerAnswers {
  growthCta: JevChoiceAnswer<JevGrowthCta>;
  growthShouldSurface: JevBooleanAnswer;
  passReason: JevChoiceAnswer<JevPassReason>;
  passIntensity: JevChoiceAnswer<JevPassIntensity>;
  showNightCta: JevChoiceAnswer<JevShowNightCta>;
  preShowFocus: JevChoiceAnswer<JevPreShowFocus>;
  homeChatFeatured: JevBooleanAnswer;
  urgency: JevScoreAnswer;
}

export interface JevGrowthDecision {
  surface: boolean;
  cta: JevGrowthCta | null;
  urgency: number | null;
  tone: "gentle" | "prominent";
  overridePrimaryAction: PartyDashboardPrimaryAction | null;
  ctaProbability: number | null;
  ctaConfidence: number | null;
  shouldSurfaceProbability: number | null;
}

export interface JevPassDecision {
  surface: boolean;
  reason: Exclude<JevPassReason, "none"> | null;
  intensity: Exclude<JevPassIntensity, "stay_quiet"> | null;
  checkoutSource: PartyPassCheckoutSource | null;
  reasonProbability: number | null;
}

export interface JevShowNightDecision {
  surface: boolean;
  cta: JevShowNightCta | null;
  urgency: number | null;
  tone: "gentle" | "prominent";
  overridePrimaryAction: PartyDashboardPrimaryAction | null;
  ctaProbability: number | null;
  ctaConfidence: number | null;
}

export interface JevPreShowDecision {
  focus: JevPreShowFocus | null;
  overridePrimaryAction: PartyDashboardPrimaryAction | null;
  focusProbability: number | null;
  focusConfidence: number | null;
}

/** Where the party-home chat preview sits relative to The Stage. */
export interface JevHomeChatDecision {
  /** True: directly under The Stage, before picks / standings / chat. */
  featured: boolean;
  probability: number | null;
}

export interface JevDecisionLayerDecision {
  source: JevDecisionSource;
  page: JevDecisionPage;
  growth: JevGrowthDecision;
  pass: JevPassDecision;
  showNight: JevShowNightDecision;
  preShow: JevPreShowDecision;
  homeChat: JevHomeChatDecision;
}

function roundHours(value: number): number {
  if (!Number.isFinite(value)) {
    return 0;
  }
  return Math.round(value * 10) / 10;
}

export function isRedCarpetDecisionWindowOpen(input: {
  hoursUntilShow: number | null;
  showIsComplete: boolean;
  announcedResultsCount: number;
}): boolean {
  if (input.showIsComplete || input.announcedResultsCount > 0) {
    return false;
  }
  const hours = input.hoursUntilShow;
  if (hours == null || !Number.isFinite(hours)) {
    return false;
  }
  return (
    hours <= JEV_RED_CARPET_HOURS_BEFORE_SHOW &&
    hours >= -JEV_RED_CARPET_HOURS_AFTER_START
  );
}

/**
 * Visual "live" treatment for the red carpet.
 * On from the same pre-show lead as the CTA window, and off at show start.
 * The CTA may still appear for a short time after start; the live dot does not.
 */
export function isRedCarpetArrivalsLive(input: {
  hasRedCarpet: boolean;
  hoursUntilShow: number | null;
  showHasStarted: boolean;
}): boolean {
  if (!input.hasRedCarpet || input.showHasStarted) {
    return false;
  }
  const hours = input.hoursUntilShow;
  if (hours == null || !Number.isFinite(hours)) {
    return false;
  }
  return hours > 0 && hours <= JEV_RED_CARPET_HOURS_BEFORE_SHOW;
}

export function isWatchRedCarpetCtaAllowed(
  signals: Pick<
    JevDecisionLayerSignals,
    | "hasRedCarpet"
    | "hoursUntilShow"
    | "showHasStarted"
    | "showIsComplete"
    | "announcedResultsCount"
    | "isLocked"
    | "userBallotComplete"
    | "hoursUntilLock"
    | "isOwner"
    | "joinedParticipantCount"
    | "plan"
    | "atOrOverCap"
    | "blockedJoinCount"
    | "page"
  >,
): boolean {
  if (!signals.hasRedCarpet || signals.page !== "party_home") {
    return false;
  }
  if (!isRedCarpetDecisionWindowOpen(signals)) {
    return false;
  }

  const urgentBallot =
    !signals.isLocked &&
    !signals.userBallotComplete &&
    signals.hoursUntilLock != null &&
    Number.isFinite(signals.hoursUntilLock) &&
    signals.hoursUntilLock <= JEV_RED_CARPET_URGENT_BALLOT_HOURS;
  if (urgentBallot) {
    return false;
  }

  const hostShouldInvite =
    signals.isOwner &&
    signals.joinedParticipantCount < JEV_RED_CARPET_GROWTH_PARTY_SIZE &&
    !signals.showHasStarted &&
    signals.hoursUntilShow != null &&
    signals.hoursUntilShow > JEV_RED_CARPET_HOST_INVITE_PRIORITY_HOURS;
  if (hostShouldInvite) {
    return false;
  }

  const hostNeedsPassRoom =
    signals.isOwner &&
    !isPaidParty(signals.plan) &&
    (signals.atOrOverCap || signals.blockedJoinCount > 0);
  if (hostNeedsPassRoom) {
    return false;
  }

  return true;
}

export function shouldHideHardcodedRedCarpetLink(input: {
  surfaceKind: JevDecisionSurfaceKind;
  growthCta: JevDecisionLayerCta | null;
  showNightCta: JevDecisionLayerCta | null;
}): boolean {
  return (
    (input.surfaceKind === "growth" &&
      input.growthCta === "watch_red_carpet") ||
    (input.surfaceKind === "show_night" &&
      input.showNightCta === "watch_red_carpet")
  );
}

export function hoursBetween(
  from: Date | string,
  to: Date | string,
): number | null {
  const fromMs = new Date(from).getTime();
  const toMs = new Date(to).getTime();
  if (Number.isNaN(fromMs) || Number.isNaN(toMs)) {
    return null;
  }
  return roundHours((toMs - fromMs) / 3_600_000);
}

export function isJevGrowthCta(value: unknown): value is JevGrowthCta {
  return (
    typeof value === "string" &&
    (JEV_GROWTH_CTAS as readonly string[]).includes(value)
  );
}

export function isJevPassReason(value: unknown): value is JevPassReason {
  return (
    typeof value === "string" &&
    (JEV_PASS_REASONS as readonly string[]).includes(value)
  );
}

export function isJevPassIntensity(value: unknown): value is JevPassIntensity {
  return (
    typeof value === "string" &&
    (JEV_PASS_INTENSITIES as readonly string[]).includes(value)
  );
}

export function isJevShowNightCta(value: unknown): value is JevShowNightCta {
  return (
    typeof value === "string" &&
    (JEV_SHOW_NIGHT_CTAS as readonly string[]).includes(value)
  );
}

export function isJevPreShowFocus(value: unknown): value is JevPreShowFocus {
  return (
    typeof value === "string" &&
    (JEV_PRE_SHOW_FOCUSES as readonly string[]).includes(value)
  );
}

export function isJevDecisionPage(value: unknown): value is JevDecisionPage {
  return (
    typeof value === "string" &&
    (JEV_DECISION_PAGES as readonly string[]).includes(value)
  );
}

export function checkoutSourceForJevPassReason(
  reason: JevPassReason | null | undefined,
): PartyPassCheckoutSource | null {
  if (!reason || reason === "none") {
    return null;
  }
  return JEV_PASS_REASON_TO_CHECKOUT_SOURCE[reason];
}

function selectedProbability<T extends string>(
  answer: JevChoiceAnswer<T>,
): number {
  const value = answer.probabilities?.[answer.choice];
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function trustedChoice<T extends string>(input: {
  value: T | null;
  probability: number;
  confidence: number;
  quietValue: T;
}): boolean {
  return (
    input.value != null &&
    input.value !== input.quietValue &&
    input.probability >= JEV_MIN_CTA_PROBABILITY &&
    input.confidence >= JEV_MIN_CTA_CONFIDENCE
  );
}

function isRedundantOnPage(
  cta: JevDecisionLayerCta | null,
  page: JevDecisionPage,
): boolean {
  if (cta == null || cta === "stay_quiet") {
    return false;
  }
  if (page === "party_vote" && cta === "vote_remaining") {
    return true;
  }
  if (page === "party_leaderboard" && cta === "follow_show") {
    return true;
  }
  if (
    page === "party_chat" &&
    (cta === "try_chat" || cta === "unlock_chat")
  ) {
    return true;
  }
  if (
    page === "party_invite" &&
    (cta === "invite_more" || cta === "share_invite" || cta === "celebrate")
  ) {
    return true;
  }
  return false;
}

function quietGrowth(): JevGrowthDecision {
  return {
    surface: false,
    cta: null,
    urgency: null,
    tone: "gentle",
    overridePrimaryAction: null,
    ctaProbability: null,
    ctaConfidence: null,
    shouldSurfaceProbability: null,
  };
}

function quietPass(): JevPassDecision {
  return {
    surface: false,
    reason: null,
    intensity: null,
    checkoutSource: null,
    reasonProbability: null,
  };
}

function quietShowNight(): JevShowNightDecision {
  return {
    surface: false,
    cta: null,
    urgency: null,
    tone: "gentle",
    overridePrimaryAction: null,
    ctaProbability: null,
    ctaConfidence: null,
  };
}

function quietPreShow(): JevPreShowDecision {
  return {
    focus: null,
    overridePrimaryAction: null,
    focusProbability: null,
    focusConfidence: null,
  };
}

function quietHomeChat(): JevHomeChatDecision {
  return {
    featured: false,
    probability: null,
  };
}

function normalizeRedCarpetMediaCount(value: number | undefined): number {
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) {
    return 0;
  }
  return Math.floor(value);
}

export function buildJevDecisionLayerSignals(
  input: JevDecisionLayerStateInput,
): JevDecisionLayerSignals {
  const now = input.now ?? new Date();
  const hoursUntilShow = input.showStart
    ? hoursBetween(now, input.showStart)
    : null;
  const atOrOverCap =
    input.memberCap != null &&
    Number.isFinite(input.memberCap) &&
    input.totalParticipantCount >= input.memberCap;
  const redCarpetMediaCount = normalizeRedCarpetMediaCount(
    input.redCarpetMediaCount,
  );

  return {
    isOwner: input.isOwner,
    plan: input.plan,
    joinedParticipantCount: input.joinedParticipantCount,
    pendingInviteCount: input.pendingInviteCount,
    hoursSinceCreated: hoursBetween(input.createdAt, now) ?? 0,
    hoursUntilShow,
    hoursUntilLock: input.votingClosesAt
      ? hoursBetween(now, input.votingClosesAt)
      : null,
    showHasStarted: input.showHasStarted,
    showIsComplete: input.showIsComplete,
    isLocked: input.isLocked,
    userBallotComplete: input.userBallotComplete,
    nonVoterCount: input.nonVoterCount,
    chatUnlocked: input.chatUnlocked,
    chatHasActivity: input.chatHasActivity,
    chatUnused: input.chatUnlocked && !input.chatHasActivity,
    atOrOverCap,
    nearMemberCap: isPartyMemberCapNearLimit(
      input.memberCap,
      input.totalParticipantCount,
    ),
    blockedJoinCount: input.blockedJoinCount,
    socialPartySizeHint: SOCIAL_PARTY_SIZE_HINT,
    announcedResultsCount: input.announcedResultsCount,
    totalCategories: input.totalCategories,
    page: input.page,
    hasRedCarpet: redCarpetMediaCount > 0,
    redCarpetMediaCount,
    redCarpetWindowOpen: isRedCarpetDecisionWindowOpen({
      hoursUntilShow,
      showIsComplete: input.showIsComplete,
      announcedResultsCount: input.announcedResultsCount,
    }),
  };
}

/**
 * Anonymized JSON blob sent to Jev. Counts and booleans only — no names,
 * emails, invite codes, party titles, or user ids.
 */
export function toJevDecisionLayerState(
  signals: JevDecisionLayerSignals,
): JevDecisionLayerSignals {
  return {
    isOwner: signals.isOwner,
    plan: signals.plan,
    joinedParticipantCount: signals.joinedParticipantCount,
    pendingInviteCount: signals.pendingInviteCount,
    hoursSinceCreated: signals.hoursSinceCreated,
    hoursUntilShow: signals.hoursUntilShow,
    hoursUntilLock: signals.hoursUntilLock,
    showHasStarted: signals.showHasStarted,
    showIsComplete: signals.showIsComplete,
    isLocked: signals.isLocked,
    userBallotComplete: signals.userBallotComplete,
    nonVoterCount: signals.nonVoterCount,
    chatUnlocked: signals.chatUnlocked,
    chatHasActivity: signals.chatHasActivity,
    chatUnused: signals.chatUnused,
    atOrOverCap: signals.atOrOverCap,
    nearMemberCap: signals.nearMemberCap,
    blockedJoinCount: signals.blockedJoinCount,
    socialPartySizeHint: signals.socialPartySizeHint,
    announcedResultsCount: signals.announcedResultsCount,
    totalCategories: signals.totalCategories,
    page: signals.page,
    hasRedCarpet: signals.hasRedCarpet,
    redCarpetMediaCount: signals.redCarpetMediaCount,
    redCarpetWindowOpen: signals.redCarpetWindowOpen,
  };
}

export function fallbackJevDecisionLayerDecision(
  source: Exclude<JevDecisionSource, "jev"> = "fallback",
  page: JevDecisionPage = "party_home",
): JevDecisionLayerDecision {
  return {
    source,
    page,
    growth: quietGrowth(),
    pass: quietPass(),
    showNight: quietShowNight(),
    preShow: quietPreShow(),
    homeChat: quietHomeChat(),
  };
}

export function mapJevDecisionLayerAnswers(input: {
  answers: JevDecisionLayerAnswers;
  confidence?: Record<string, number> | undefined;
  signals: JevDecisionLayerSignals;
}): JevDecisionLayerDecision {
  const { answers, signals } = input;
  const page = isJevDecisionPage(signals.page) ? signals.page : "party_home";
  const urgency = Number.isFinite(answers.urgency.score)
    ? answers.urgency.score
    : 0;
  const tone: "gentle" | "prominent" =
    urgency >= JEV_PROMINENT_URGENCY_SCORE ? "prominent" : "gentle";

  const growthCta = isJevGrowthCta(answers.growthCta.choice)
    ? answers.growthCta.choice
    : null;
  const growthCtaProbability = growthCta
    ? selectedProbability(answers.growthCta)
    : 0;
  const growthCtaConfidence =
    typeof input.confidence?.growthCta === "number" &&
    Number.isFinite(input.confidence.growthCta)
      ? input.confidence.growthCta
      : 0;
  const growthShouldSurfaceProbability = answers.growthShouldSurface.probability;
  const growthAllowedForViewer = Boolean(
    growthCta &&
      (!HOST_ONLY_GROWTH_CTAS.has(growthCta) || signals.isOwner) &&
      (growthCta !== "try_chat" || signals.chatUnlocked) &&
      (growthCta !== "watch_red_carpet" || isWatchRedCarpetCtaAllowed(signals)),
  );
  const trustedGrowth =
    trustedChoice({
      value: growthCta,
      probability: growthCtaProbability,
      confidence: growthCtaConfidence,
      quietValue: "stay_quiet",
    }) &&
    growthAllowedForViewer &&
    !isRedundantOnPage(growthCta, page);
  const growthSurface =
    growthShouldSurfaceProbability >= JEV_MIN_SURFACE_PROBABILITY &&
    trustedGrowth;

  const rawPassReason = isJevPassReason(answers.passReason.choice)
    ? answers.passReason.choice
    : "none";
  const passReasonProbability = selectedProbability(answers.passReason);
  const passReasonConfidence =
    typeof input.confidence?.passReason === "number" &&
    Number.isFinite(input.confidence.passReason)
      ? input.confidence.passReason
      : passReasonProbability;
  const trustedPassReason =
    !isPaidParty(signals.plan) &&
    rawPassReason !== "none" &&
    passReasonProbability >= JEV_MIN_CTA_PROBABILITY &&
    passReasonConfidence >= JEV_MIN_CTA_CONFIDENCE
      ? rawPassReason
      : null;
  const rawPassIntensity = isJevPassIntensity(answers.passIntensity.choice)
    ? answers.passIntensity.choice
    : "stay_quiet";
  const passIntensityProbability = selectedProbability(answers.passIntensity);
  const passIntensityConfidence =
    typeof input.confidence?.passIntensity === "number" &&
    Number.isFinite(input.confidence.passIntensity)
      ? input.confidence.passIntensity
      : passIntensityProbability;
  const trustedPassIntensity: Exclude<JevPassIntensity, "stay_quiet"> | null =
    trustedPassReason != null &&
    rawPassIntensity !== "stay_quiet" &&
    trustedChoice({
      value: rawPassIntensity,
      probability: passIntensityProbability,
      confidence: passIntensityConfidence,
      quietValue: "stay_quiet",
    })
      ? rawPassIntensity
      : null;

  const showNightCtaRaw = isJevShowNightCta(answers.showNightCta.choice)
    ? answers.showNightCta.choice
    : null;
  const showNightCta =
    showNightCtaRaw === "unlock_chat" && isPaidParty(signals.plan)
      ? "try_chat"
      : showNightCtaRaw;
  const showNightCtaProbability = showNightCtaRaw
    ? selectedProbability(answers.showNightCta)
    : 0;
  const showNightCtaConfidence =
    typeof input.confidence?.showNightCta === "number" &&
    Number.isFinite(input.confidence.showNightCta)
      ? input.confidence.showNightCta
      : 0;
  const showNightAllowed =
    signals.showHasStarted &&
    (showNightCta !== "try_chat" || signals.chatUnlocked) &&
    (showNightCta !== "unlock_chat" || !isPaidParty(signals.plan)) &&
    (showNightCta !== "watch_red_carpet" ||
      isWatchRedCarpetCtaAllowed(signals));
  const showNightSurface =
    showNightAllowed &&
    trustedChoice({
      value: showNightCta,
      probability: showNightCtaProbability,
      confidence: showNightCtaConfidence,
      quietValue: "stay_quiet",
    }) &&
    !isRedundantOnPage(showNightCta, page);

  const preShowAnswer = answers.preShowFocus;
  const preShowFocus = isJevPreShowFocus(preShowAnswer?.choice)
    ? preShowAnswer.choice
    : null;
  const preShowFocusProbability = preShowAnswer
    ? selectedProbability(preShowAnswer)
    : 0;
  const preShowFocusConfidence =
    typeof input.confidence?.preShowFocus === "number" &&
    Number.isFinite(input.confidence.preShowFocus)
      ? input.confidence.preShowFocus
      : 0;
  const preShowApplicable =
    !signals.showHasStarted && !signals.showIsComplete;
  const trustedPreShow =
    preShowApplicable &&
    preShowFocus != null &&
    preShowFocusProbability >= JEV_MIN_CTA_PROBABILITY &&
    preShowFocusConfidence >= JEV_MIN_CTA_CONFIDENCE;
  const preShowOverride: PartyDashboardPrimaryAction | null =
    trustedPreShow && preShowFocus === "grow"
      ? "invite"
      : trustedPreShow &&
          preShowFocus === "vote" &&
          !signals.userBallotComplete &&
          !signals.isLocked
        ? "vote"
        : null;

  const homeChatProbability = Number.isFinite(answers.homeChatFeatured?.probability)
    ? answers.homeChatFeatured.probability
    : 0;
  const homeChatConfidence =
    typeof input.confidence?.homeChatFeatured === "number" &&
    Number.isFinite(input.confidence.homeChatFeatured)
      ? input.confidence.homeChatFeatured
      : homeChatProbability;
  const homeChatFeatured =
    page === "party_home" &&
    signals.chatUnlocked &&
    homeChatProbability >= JEV_MIN_SURFACE_PROBABILITY &&
    homeChatConfidence >= JEV_MIN_CTA_CONFIDENCE;

  const canOverride =
    growthSurface &&
    urgency >= JEV_OVERRIDE_URGENCY_SCORE &&
    growthCta != null &&
    CTA_TO_PRIMARY_ACTION[growthCta] != null;

  return {
    source: "jev",
    page,
    growth: {
      surface: growthSurface,
      cta: growthSurface ? growthCta : null,
      urgency: growthSurface ? urgency : null,
      tone: growthSurface ? tone : "gentle",
      overridePrimaryAction: canOverride
        ? (CTA_TO_PRIMARY_ACTION[growthCta] ?? null)
        : null,
      ctaProbability: growthCtaProbability,
      ctaConfidence: growthCtaConfidence,
      shouldSurfaceProbability: growthShouldSurfaceProbability,
    },
    pass: {
      surface: trustedPassIntensity != null,
      reason: trustedPassReason,
      intensity: trustedPassIntensity,
      checkoutSource:
        trustedPassIntensity != null
          ? checkoutSourceForJevPassReason(trustedPassReason)
          : null,
      reasonProbability: passReasonProbability,
    },
    showNight: {
      surface: showNightSurface,
      cta: showNightSurface ? showNightCta : null,
      urgency: showNightSurface ? urgency : null,
      tone: showNightSurface ? tone : "gentle",
      overridePrimaryAction:
        showNightSurface &&
        showNightCta != null &&
        SHOW_NIGHT_CTA_TO_PRIMARY[showNightCta] != null
          ? (SHOW_NIGHT_CTA_TO_PRIMARY[showNightCta] ?? null)
          : null,
      ctaProbability: showNightCtaProbability,
      ctaConfidence: showNightCtaConfidence,
    },
    preShow: {
      focus: trustedPreShow ? preShowFocus : null,
      overridePrimaryAction: preShowOverride,
      focusProbability: preShowFocusProbability,
      focusConfidence: preShowFocusConfidence,
    },
    homeChat: {
      featured: homeChatFeatured,
      probability: homeChatProbability,
    },
  };
}

export function resolveDashboardPrimaryAction(
  dashboardAction: PartyDashboardPrimaryAction,
  decision: JevDecisionLayerDecision,
): PartyDashboardPrimaryAction {
  return (
    decision.showNight.overridePrimaryAction ??
    decision.preShow.overridePrimaryAction ??
    decision.growth.overridePrimaryAction ??
    dashboardAction
  );
}

export function pickDecisionLayerSurface(input: {
  page: JevDecisionPage;
  growthSurface: boolean;
  showNightSurface: boolean;
  passSurface: boolean;
  passIntensity: JevPassIntensity | null;
  existingLoudPassSurface: boolean;
  showNightCta?: JevShowNightCta | null;
}): JevDecisionSurfaceKind {
  const canInterruptPass =
    input.passSurface &&
    input.passIntensity === "interrupt" &&
    !input.existingLoudPassSurface &&
    input.page !== "party_vote";

  if (
    canInterruptPass &&
    input.showNightSurface &&
    input.showNightCta === "unlock_chat"
  ) {
    return "pass_interrupt";
  }
  if (input.showNightSurface) {
    return "show_night";
  }
  if (input.page === "party_chat") {
    return "none";
  }
  if (canInterruptPass) {
    return "pass_interrupt";
  }
  if (input.growthSurface) {
    return "growth";
  }
  if (
    input.passSurface &&
    input.passIntensity === "soft_suggest" &&
    !input.existingLoudPassSurface
  ) {
    return "pass_soft";
  }
  return "none";
}

export interface DecisionLayerHrefOptions {
  awardShowId?: string | null;
}

export interface JevPrimaryDecisionClick {
  brain: "show_night" | "growth" | "pre_show";
  surfaceKind: JevDecisionSurfaceKind;
  cta: JevDecisionLayerCta | null;
  tone: "gentle" | "prominent" | null;
  preShowFocus: JevPreShowFocus | null;
  passReason: Exclude<JevPassReason, "none"> | null;
  passIntensity: Exclude<JevPassIntensity, "stay_quiet"> | null;
}

/** Which Jev decision replaced the home primary button, if one did. */
export function jevPrimaryDecisionClick(
  decision: JevDecisionLayerDecision,
): JevPrimaryDecisionClick | null {
  const passReason = decision.pass.reason;
  const passIntensity = decision.pass.intensity;

  if (decision.showNight.overridePrimaryAction) {
    return {
      brain: "show_night",
      surfaceKind: "show_night",
      cta: decision.showNight.cta,
      tone: decision.showNight.tone,
      preShowFocus: null,
      passReason,
      passIntensity,
    };
  }
  if (decision.preShow.overridePrimaryAction && decision.preShow.focus) {
    return {
      brain: "pre_show",
      surfaceKind: "none",
      cta: null,
      tone: null,
      preShowFocus: decision.preShow.focus,
      passReason,
      passIntensity,
    };
  }
  if (decision.growth.overridePrimaryAction && decision.growth.cta) {
    return {
      brain: "growth",
      surfaceKind: "growth",
      cta: decision.growth.cta,
      tone: decision.growth.tone,
      preShowFocus: null,
      passReason,
      passIntensity,
    };
  }
  return null;
}

export function hrefForDashboardPrimaryAction(input: {
  action: PartyDashboardPrimaryAction;
  partyId: string;
  decision: JevDecisionLayerDecision;
}): string {
  if (input.action === "chat" && jevPrimaryDecisionClick(input.decision)) {
    return `/parties/${input.partyId}/chat?entry=decision_layer`;
  }
  return getPrimaryActionHref(input.action, input.partyId);
}

export function getDecisionLayerHref(
  cta: JevDecisionLayerCta,
  partyId: string,
  options?: DecisionLayerHrefOptions,
): string {
  switch (cta) {
    case "try_chat":
    case "unlock_chat":
      return `/parties/${partyId}/chat?entry=decision_layer`;
    case "nudge_voters":
      return getPartyParticipantsHref(partyId, { focusNonVoters: true });
    case "vote_remaining":
      return `/parties/${partyId}/vote`;
    case "follow_show":
      return `/parties/${partyId}/leaderboard`;
    case "react_live":
      return `/parties/${partyId}#live-results`;
    case "invite_more":
    case "share_invite":
    case "celebrate":
      return `/parties/${partyId}/invite`;
    case "watch_red_carpet": {
      const awardShowId = options?.awardShowId?.trim();
      if (!awardShowId) {
        return `/parties/${partyId}`;
      }
      return `/award-shows/${awardShowId}?tab=red-carpet&src=decision_layer`;
    }
    case "stay_quiet":
    default:
      return `/parties/${partyId}`;
  }
}
